import { ThinkingStep } from "@cohesive/ui-components";
import { useEffect, useMemo, useRef, useState } from "react";

interface CollapsableThinkingBlockProps {
  content: string;
  isStreaming: boolean;
  /** From API / thinking_delta event when done. */
  thinkingDurationMilliseconds?: number;
}

function formatThoughtLabel(ms: number): string {
  const seconds = Math.round(ms / 1000);
  // Sub-second reasoning reads oddly as "Thought for 0s".
  return seconds === 0 ? "Thought briefly" : `Thought for ${seconds}s`;
}

/**
 * Reasoning output as the design's activity-rail thinking step. Collapsed by
 * default: while the model is thinking it shows only a live count-up timer
 * ("Thinking 0s, 1s, 2s…"), and once done a "Thought for Ns" label. Clicking
 * the row expands the muted reasoning paragraphs.
 */
export function CollapsableThinkingBlock({
  content,
  isStreaming,
  thinkingDurationMilliseconds,
}: CollapsableThinkingBlockProps) {
  const paras = useMemo(
    () =>
      content
        .split(/\n{2,}/)
        .map((p) => p.trim())
        .filter(Boolean),
    [content],
  );

  // Live count-up while the model is thinking. Anchored to the first streaming
  // render so the label ticks 0s, 1s, 2s… and freezes when streaming ends.
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const startRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isStreaming) {
      startRef.current = null;
      return;
    }
    if (startRef.current === null) startRef.current = performance.now();
    const tick = () =>
      setElapsedSeconds(
        Math.floor((performance.now() - (startRef.current ?? 0)) / 1000),
      );
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [isStreaming]);

  if (paras.length === 0) return null;

  const label = isStreaming
    ? `Thinking ${elapsedSeconds}s`
    : typeof thinkingDurationMilliseconds === "number"
      ? formatThoughtLabel(thinkingDurationMilliseconds)
      : "Thoughts";

  return (
    <div className="px-4 py-2">
      <ThinkingStep paras={paras} label={label} last />
    </div>
  );
}
